import AccountTreeIcon from "@mui/icons-material/AccountTree";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import ControlPointOutlinedIcon from "@mui/icons-material/ControlPointOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import RemoveCircleOutlineOutlinedIcon from "@mui/icons-material/RemoveCircleOutlineOutlined";
import ReplayIcon from "@mui/icons-material/Replay";
import TuneIcon from "@mui/icons-material/Tune";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { isSearchParamsObjectEmpty } from "../../../helpers/methods";
import useFilterSearchParams from "../../../hooks/useFilterSearchParams";
import OpenCloseFilterButton from "../../button/OpenCloseFilterButton";
import SearchButton from "../../button/SearchButton/SearchButton";
import SplitButton from "../../button/SplitButton";

import { ThemeProvider } from "@emotion/react";
import { useTheme } from "@mui/material";
import { centerVericalAlignIconStyle, overflowButtonStyle } from "../../../helpers/styles";

export default function UniversalToolBar(props) {
    const theme = useTheme();

    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();

    const { clearSearchParamsByFilterPrefix, getSearchParamsByFilterPrefix } =
        useFilterSearchParams(searchParams);

    const isFilterSet =
        props.isFilterSet !== undefined
            ? props.isFilterSet
            : !isSearchParamsObjectEmpty(getSearchParamsByFilterPrefix(props.filterPrefix));

    const buttonStyle = {
        ...overflowButtonStyle,
        minHeight: "2rem",
        height: "auto",
    };

    return (
        <ThemeProvider theme={theme}>
            <Grid
                container
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                columnSpacing={0}
                rowSpacing={2}
                style={props.style}
            >
                {props.showCreateButton && (
                    <Grid item xs={12} md={props.mdGridButton}>
                        <Button
                            fullWidth
                            variant="contained"
                            size="small"
                            startIcon={
                                <ControlPointOutlinedIcon sx={centerVericalAlignIconStyle} />
                            }
                            color="success"
                            onClick={props.onClickCreateItem}
                            sx={buttonStyle}
                        >
                            {props.createButtonLabel
                                ? props.createButtonLabel
                                : props.itemType === "lead"
                                  ? t("bar.tool_bar.create_lead")
                                  : t("bar.tool_bar.create")}
                        </Button>
                    </Grid>
                )}
                {props.showRemoveLeadButton && (
                    <Grid item xs={12} md={props.mdGridButton}>
                        <Button
                            fullWidth
                            variant="contained"
                            size="small"
                            color="error"
                            disabled={props.disableRemoveLeadButton}
                            startIcon={
                                <RemoveCircleOutlineOutlinedIcon sx={centerVericalAlignIconStyle} />
                            }
                            onClick={props.onClickRemoveLead}
                            sx={buttonStyle}
                        >
                            {t("bar.tool_bar.remove_lead")}
                        </Button>
                    </Grid>
                )}
                {props.showImportList && (
                    <Grid item xs={12} md={props.mdGridButton}>
                        <Button
                            fullWidth
                            variant="contained"
                            size="small"
                            startIcon={<FileUploadOutlinedIcon sx={centerVericalAlignIconStyle} />}
                            onClick={props.onClickImportList}
                            sx={{
                                ...buttonStyle,
                                bgcolor: "primary.light",
                            }}
                        >
                            {t("bar.tool_bar.import_list")}
                        </Button>
                    </Grid>
                )}
                {props.showAddCustomerPersonButton && (
                    <Grid item xs={12} md={props.mdGridButton}>
                        <Button
                            fullWidth
                            variant="contained"
                            size="small"
                            color="success"
                            startIcon={
                                <PersonOutlineOutlinedIcon sx={centerVericalAlignIconStyle} />
                            }
                            onClick={props.onClickAddCustomerPerson}
                            sx={buttonStyle}
                        >
                            {t("bar.tool_bar.add_customer_person")}
                        </Button>
                    </Grid>
                )}
                {props.showAddCustomerCompanyButton && (
                    <Grid item xs={12} md={props.mdGridButton}>
                        <Button
                            fullWidth
                            variant="contained"
                            size="small"
                            startIcon={<BusinessOutlinedIcon sx={centerVericalAlignIconStyle} />}
                            onClick={props.onClickAddCustomerCompany}
                            sx={{
                                ...buttonStyle,
                                bgcolor: "primary.bright",
                            }}
                        >
                            {t("bar.tool_bar.add_customer_company")}
                        </Button>
                    </Grid>
                )}
                {props.extraEditButtonList.map((button, idx) => (
                    <Grid
                        item
                        xs={12}
                        md={
                            props.extraButtonListMdGrid
                                ? props.extraButtonListMdGrid[idx]
                                : props.mdGridExtraButton || props.mdGridButton
                        }
                    >
                        {button}
                    </Grid>
                ))}
                <Grid item xs={12} md={props.mdGridButton}>
                    <Button
                        fullWidth
                        variant="contained"
                        size="small"
                        startIcon={<TuneIcon sx={centerVericalAlignIconStyle} />}
                        onClick={props.onClickAdjustTable}
                        sx={buttonStyle}
                    >
                        {t("bar.tool_bar.adjust_table")}
                    </Button>
                </Grid>
                {props.showMyFiltersButton && (
                    <Grid item xs={12} md={props.mdGridButton}>
                        <Button
                            fullWidth
                            variant="contained"
                            size="small"
                            startIcon={<FilterAltIcon sx={centerVericalAlignIconStyle} />}
                            onClick={props.onClickMyFilters}
                            color={isFilterSet ? "success" : "primary"}
                            sx={buttonStyle}
                        >
                            {t("bar.tool_bar.my_filters")}
                        </Button>
                    </Grid>
                )}
                {props.showCleanfiltersButton && (
                    <Grid item xs={12} md={props.mdGridButton}>
                        <Button
                            fullWidth
                            variant="contained"
                            size="small"
                            startIcon={<ReplayIcon sx={centerVericalAlignIconStyle} />}
                            onClick={() =>
                                setSearchParams(clearSearchParamsByFilterPrefix(props.filterPrefix))
                            }
                            sx={buttonStyle}
                        >
                            {t("bar.tool_bar.clear_filters")}
                        </Button>
                    </Grid>
                )}
                {props.showMassActionButton && (
                    <Grid item xs={12} md={props.mdGridButton}>
                        <SplitButton
                            startIcon={<AccountTreeIcon sx={centerVericalAlignIconStyle} />}
                            label={t("bar.tool_bar.mass_actions")}
                            options={props.massActionButtonOptions}
                        />
                    </Grid>
                )}

                {props.showOpenCloseFilterButton && (
                    <Grid item xs={12} md={props.mdGridButton}>
                        <OpenCloseFilterButton filterPrefix={props.filterPrefix} />
                    </Grid>
                )}

                {props.extraButtonList.map((button, idx) => (
                    <Grid
                        item
                        xs={12}
                        md={
                            props.extraButtonListMdGrid
                                ? props.extraButtonListMdGrid[idx]
                                : props.mdGridExtraButton || props.mdGridButton
                        }
                    >
                        {button}
                    </Grid>
                ))}

                {props.showSearchButton && (
                    <Grid item xs={12} md={0.5} textAlign={{ xs: "center", md: "right" }}>
                        <SearchButton onClickSearch={props.onClickSearch} />
                    </Grid>
                )}
            </Grid>
        </ThemeProvider>
    );
}

UniversalToolBar.propTypes = {
    style: PropTypes.object,
    onClickAdjustTable: PropTypes.func,
    onClickMyFilters: PropTypes.func,
    onClickCreateItem: PropTypes.func,
    massActionButtonOptions: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string,
            callback: PropTypes.func,
        }),
    ),
    onClickOpenCloseFilter: PropTypes.func,
    onClickSearch: PropTypes.func,
    itemType: PropTypes.oneOf(["foo", "lead", "customer"]),
    showMassActionButton: PropTypes.bool,
    showCreateButton: PropTypes.bool,
    showOpenCloseFilterButton: PropTypes.bool,
    showCleanfiltersButton: PropTypes.bool,
    showMyFiltersButton: PropTypes.bool,
    showSearchButton: PropTypes.bool,
    showRemoveLeadButton: PropTypes.bool,
    disableRemoveLeadButton: PropTypes.bool,
    showImportList: PropTypes.bool,
    showAddCustomerPersonButton: PropTypes.bool,
    showAddCustomerCompanyButton: PropTypes.bool,
    mdGridButton: PropTypes.number,
    mdGridExtraButton: PropTypes.number,
    extraButtonList: PropTypes.array,
    extraButtonListMdGrid: PropTypes.array,
    filterPrefix: PropTypes.string,
    isFilterSet: PropTypes.bool,
    createButtonLabel: PropTypes.string,
    extraEditButtonList: PropTypes.array,
    onClickRemoveLead: PropTypes.func,
    onClickImportList: PropTypes.func,
    onClickAddCustomerPerson: PropTypes.func,
    onClickAddCustomerCompany: PropTypes.func,
};

UniversalToolBar.defaultProps = {
    itemType: "foo",
    showMassActionButton: true,
    showCreateButton: true,
    showCleanfiltersButton: true,
    showOpenCloseFilterButton: true,
    showMyFiltersButton: true,
    showSearchButton: true,
    showRemoveLeadButton: false,
    disableRemoveLeadButton: true,
    showImportList: false,
    showAddCustomerPersonButton: false,
    showAddCustomerCompanyButton: false,
    mdGridButton: 1.75,
    extraButtonList: [],
    extraEditButtonList: [],
};
